import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PageHeader from "@/components/PageHeader";
import Seo from "@/components/Seo";
import BrochureCta from "@/components/BrochureCta";
import VoltechGatesIntro from "@/components/VoltechGatesIntro";
import DealCountdown from "@/components/DealCountdown";
import StarRating from "@/components/StarRating";
import TrustBar from "@/components/TrustBar";
import { api, fileUrl } from "@/lib/api";
import { waLink } from "@/lib/whatsapp";
import { COMPANY } from "@/data/site";
import {
  ArrowUpRight,
  Check,
  X,
  MessageCircle,
  Truck,
  ShieldCheck,
  RefreshCw,
  Zap,
  Flame,
  Eye,
} from "lucide-react";

function formatINR(n) {
  if (n == null) return null;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

function discountPct(mrp, price) {
  if (!mrp || !price || mrp <= price) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState("featured");
  const [gatesDone, setGatesDone] = useState(false);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(products.map((p) => p.category)))],
    [products]
  );

  const filtered = useMemo(() => {
    let list = filter === "All" ? [...products] : products.filter((p) => p.category === filter);
    if (sort === "price_low") {
      list.sort((a, b) => (a.price ?? 9e9) - (b.price ?? 9e9));
    } else if (sort === "price_high") {
      list.sort((a, b) => (b.price ?? -1) - (a.price ?? -1));
    } else if (sort === "popular") {
      list.sort((a, b) => (b.views || 0) - (a.views || 0));
    } else {
      // featured then rest
      list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
    return list;
  }, [products, filter, sort]);

  // Product ItemList JSON-LD for SEO
  const itemListLd = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "VOLTECH Power Solutions — Products",
      itemListElement: filtered.slice(0, 25).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://voltechpowersolutions.in/products/${p.id}`,
        name: p.name,
      })),
    };
  }, [filtered]);

  return (
    <div data-testid="page-products">
      <VoltechGatesIntro onOpen={() => setGatesDone(true)} />

      <Seo
        path="/products"
        title="Shop · Lithium Batteries, Solar Lights, EV Packs"
        description="VOLTECH shop — lithium battery packs, LiFePO4 EV batteries, solar street lights, industrial backup batteries, custom packs. Factory-direct pricing, 5-year warranty, free voltech.live monitoring."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>
      </Helmet>

      <PageHeader
        eyebrow="Shop · Factory-direct"
        title="Every battery, every wattage — built in Hyderabad."
        subtitle="Live catalogue of our lithium batteries, solar street lights and custom packs. Every product ships with a 5-year warranty and free voltech.live monitoring."
        testId="products-page-header"
      />

      <DealCountdown />

      <TrustBar />

      {/* Value strip */}
      <section className="border-b border-white/10 bg-[#0a0a0c]" data-testid="shop-value-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Truck, l: "Pan-India delivery", s: "5 working days" },
            { icon: ShieldCheck, l: "5-year warranty", s: "Every battery" },
            { icon: RefreshCw, l: "Free monitoring", s: "voltech.live included" },
            { icon: Zap, l: "Factory-direct", s: "No middleman" },
          ].map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={i} className="flex items-start gap-3" data-testid={`value-item-${i}`}>
                <Icon size={20} className="text-[#e63946] mt-0.5" />
                <div>
                  <div className="text-sm font-display uppercase tracking-tight leading-tight">
                    {v.l}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-[0.15em] mt-0.5">
                    {v.s}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Filters + sort */}
      <section className="py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="products-filter-bar">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`products-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                  filter === c
                    ? "border-[#e63946] text-[#e63946] bg-[#e63946]/5"
                    : "border-white/15 text-zinc-400 hover:text-white hover:border-white/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2" data-testid="products-sort-bar">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Sort
            </span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-[#121215] border border-white/15 text-white text-xs px-3 py-2 font-mono uppercase tracking-[0.15em]"
              data-testid="products-sort-select"
            >
              <option value="featured">Featured</option>
              <option value="popular">Most popular</option>
              <option value="price_low">Price · low to high</option>
              <option value="price_high">Price · high to low</option>
            </select>
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading || !gatesDone ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="industrial-card h-96 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="products-empty">
              <div className="eyebrow">No products yet</div>
              <p className="text-zinc-400 mt-3">Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p, idx) => {
                const dp = discountPct(p.mrp, p.price);
                const wa = waLink(
                  `Hello VPS, I want to buy: ${p.name}${
                    p.price ? ` (${formatINR(p.price)})` : ""
                  }. Please share your best price.`
                );
                return (
                  <div
                    key={p.id}
                    className="industrial-card group overflow-hidden flex flex-col relative"
                    data-testid={`product-card-${idx}`}
                  >
                    {/* corner badges */}
                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
                      {p.badge && (
                        <span
                          className="bg-black/80 text-[#e63946] font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-[#e63946]/40"
                          data-testid={`product-badge-${idx}`}
                        >
                          <Flame size={10} className="inline mr-1" />
                          {p.badge}
                        </span>
                      )}
                      {dp > 0 && (
                        <span
                          className="bg-[#e63946] text-white font-display uppercase text-xs px-2 py-1"
                          data-testid={`product-discount-${idx}`}
                        >
                          Save {dp}%
                        </span>
                      )}
                    </div>
                    {p.featured && (
                      <div className="absolute top-3 right-3 z-10">
                        <span className="bg-white text-black font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1">
                          ★ Featured
                        </span>
                      </div>
                    )}

                    <Link to={`/products/${p.id}`} className="block">
                      {p.image_url ? (
                        <div className="aspect-[4/3] overflow-hidden border-b border-white/10 bg-[#0a0a0c]">
                          <img
                            src={fileUrl(p.image_url)}
                            alt={p.name}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        </div>
                      ) : (
                        <div className="aspect-[4/3] bg-[#121215] flex items-center justify-center text-zinc-700 border-b border-white/10">
                          No image
                        </div>
                      )}
                    </Link>

                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center justify-between">
                        <span className="tag-chip tag-chip-red">{p.category}</span>
                        {(p.views || 0) > 0 && (
                          <span className="text-[10px] text-zinc-500 font-mono flex items-center gap-1" data-testid={`product-views-${idx}`}>
                            <Eye size={10} /> {p.views}
                          </span>
                        )}
                      </div>
                      <Link to={`/products/${p.id}`} className="block">
                        <h3 className="font-display text-lg uppercase mt-3 leading-tight group-hover:text-[#e63946] transition-colors">
                          {p.name}
                        </h3>
                      </Link>
                      {p.rating > 0 && (
                        <div className="mt-2">
                          <StarRating rating={p.rating} count={p.rating_count || 0} testId={`product-rating-${idx}`} />
                        </div>
                      )}
                      <p className="text-sm text-zinc-400 mt-2 leading-relaxed line-clamp-2">
                        {p.short_description}
                      </p>

                      <div className="mt-4 pt-4 border-t border-white/10 flex items-end justify-between gap-3">
                        <div>
                          {p.price ? (
                            <>
                              <div className="flex items-baseline gap-2">
                                <span className="font-display text-xl text-white">
                                  {formatINR(p.price)}
                                </span>
                                {p.mrp && p.mrp > p.price && (
                                  <span className="text-xs text-zinc-500 line-through" data-testid={`product-mrp-${idx}`}>
                                    {formatINR(p.mrp)}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-[0.15em] mt-1">
                                {p.in_stock ? "In stock · ships in 3-5 days" : "Made to order"}
                              </div>
                            </>
                          ) : (
                            <span className="font-mono text-xs text-zinc-300">
                              {p.price_text || "On request"}
                            </span>
                          )}
                        </div>
                        <a
                          href={wa}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary !py-2 !px-3 text-[11px]"
                          data-testid={`product-buy-${idx}`}
                          aria-label={`Buy ${p.name} on WhatsApp`}
                        >
                          <MessageCircle size={12} /> Buy
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-16">
            <BrochureCta testId="products-brochure-cta" />
          </div>

          {/* Store-value re-assurance */}
          <div className="mt-16 industrial-card p-8 md:p-12 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="shop-reassurance">
            <div>
              <div className="eyebrow">Quality promise</div>
              <div className="font-display text-xl uppercase mt-3">
                Made in Hyderabad. Tested for India.
              </div>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                Every battery is designed for Indian voltage, temperature and usage patterns —
                not imported and relabelled.
              </p>
            </div>
            <div>
              <div className="eyebrow">Buying in bulk?</div>
              <div className="font-display text-xl uppercase mt-3">
                Slab pricing for 10+ / 50+ units
              </div>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                RWAs, factories, municipalities — get custom quotes and installation support.
              </p>
              <a
                href={waLink("Hello VPS, I'd like bulk pricing. Quantity + product:")}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary mt-4"
                data-testid="shop-bulk-cta"
              >
                <MessageCircle size={14} /> Get bulk pricing
              </a>
            </div>
            <div className="industrial-card p-5 border-[#e63946]/40">
              <ul className="space-y-2 text-sm text-zinc-200">
                <li className="flex items-start gap-2"><Check size={14} className="text-[#25d366] mt-1" /> No advance for stock items under ₹50k</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-[#25d366] mt-1" /> GST invoice + IS 16893 test report</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-[#25d366] mt-1" /> Free replacement in year 1 if defective</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-[#25d366] mt-1" /> Engineers on WhatsApp, not a chatbot</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
